import type { CostReport, HealthReport, QualityReport, UsageReport, UsageRole } from "@esi/shared";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  formatBytes,
  formatDay,
  formatNumber,
  formatPercent,
  formatUsd,
  LineChart,
  Meter,
  ShareBar,
  StackedBarChart,
  StatGrid,
  StatTile,
} from "@/components/charts";
import { Notice } from "@/components/Notice";
import { currentLocale } from "../../i18n/i18n.ts";
import { auditPath, Link } from "../../router.tsx";
import { useAdminApi } from "../admin-api.tsx";
import { FigureTable, Freshness, Section, useProjectName } from "./parts.tsx";

/** The day with the highest value, or null when every day is zero. */
function peakOf(daily: readonly { day: string; value: number }[]) {
  return daily.reduce<{ day: string; value: number } | null>(
    (best, d) => (d.value > (best?.value ?? 0) ? d : best),
    null,
  );
}

const Empty = ({ children }: { children: string }) => (
  <p className="rounded-lg border border-dashed border-line px-4 py-6 text-sm text-fg-muted">
    {children}
  </p>
);

// ── Usage ────────────────────────────────────────────────────────────────────────────────

export function UsageTab({ report }: { report: UsageReport }) {
  const { t } = useTranslation();
  const projectName = useProjectName();
  const days = report.daily.length;
  const peak = peakOf(report.daily.map((d) => ({ day: d.day, value: d.questions })));
  // The month the period ends in, by name: never another month's figure under its label.
  const monthStart = `${report.period.to.slice(0, 7)}-01`;
  const month = report.monthly.find((m) => m.start === monthStart);
  const monthName = new Intl.DateTimeFormat(currentLocale(), {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${monthStart}T00:00:00Z`));
  const entities = report.entities.filter((e) => e.questions > 0);
  const topEntity = [...entities].sort((a, b) => b.questions - a.questions)[0];
  const top = report.topProjects[0];
  return (
    <div className="flex flex-col gap-8">
      <Freshness freshness={report.freshness} synthetic={report.synthetic} />
      <StatGrid>
        <StatTile
          label={t("reports.usage.questions")}
          value={formatNumber(report.totals.questions)}
        />
        <StatTile
          label={t("reports.usage.conversations")}
          value={formatNumber(report.totals.conversations)}
        />
        <StatTile
          label={t("reports.usage.activeUsersIn", { month: monthName })}
          value={formatNumber(month?.activeUsers ?? 0)}
        />
      </StatGrid>
      <LineChart
        title={t("reports.usage.dailyTitle")}
        summary={
          peak
            ? t("reports.usage.dailySummary", {
                questions: formatNumber(report.totals.questions),
                days,
                peak: formatDay(peak.day),
              })
            : t("reports.usage.dailySummaryEmpty", { days })
        }
        series={[
          { key: "questions", label: t("reports.usage.questions") },
          { key: "activeUsers", label: t("reports.usage.activeUsers") },
        ]}
        data={report.daily.map((d) => ({
          label: d.day,
          values: { questions: d.questions, activeUsers: d.activeUsers },
        }))}
        formatLabel={formatDay}
        labelHeader={t("reports.day")}
      />
      {topEntity ? (
        <BarChart
          title={t("reports.usage.entitiesTitle")}
          summary={t("reports.usage.entitiesSummary", {
            top: topEntity.name,
            count: formatNumber(topEntity.questions),
          })}
          data={entities.map((e) => ({ label: e.name, value: e.questions }))}
          labelHeader={t("reports.entity")}
          valueHeader={t("reports.usage.questions")}
        />
      ) : null}
      <Section title={t("reports.usage.topProjects")}>
        {top ? (
          <>
            <p className="text-sm text-fg-secondary">
              {t("reports.usage.topProjectsSummary", {
                top: projectName(top),
                count: formatNumber(top.questions),
              })}
            </p>
            <FigureTable
              caption={t("reports.usage.topProjects")}
              columns={[
                t("reports.project"),
                t("reports.usage.questions"),
                t("reports.usage.conversations"),
              ]}
              rows={report.topProjects.map((p) => ({
                key: p.projectId ?? (p.standalone ? `standalone:${p.entityId}` : "personal"),
                cells: [projectName(p), formatNumber(p.questions), formatNumber(p.conversations)],
              }))}
            />
          </>
        ) : (
          <Empty>{t("reports.empty")}</Empty>
        )}
      </Section>
    </div>
  );
}

// ── Answer quality ───────────────────────────────────────────────────────────────────────

export function QualityTab({ report }: { report: QualityReport }) {
  const { t } = useTranslation();
  const projectName = useProjectName();
  const { totals } = report;
  const citations = totals.supported + totals.unverified + totals.unsupported;
  return (
    <div className="flex flex-col gap-8">
      <Freshness freshness={report.freshness} synthetic={report.synthetic} />
      <StatGrid>
        <StatTile label={t("reports.quality.answers")} value={formatNumber(totals.answers)} />
        <StatTile
          label={t("reports.quality.withoutSource")}
          value={formatNumber(totals.answersWithoutSource)}
          hint={t("reports.quality.withoutSourceHint", {
            share: formatPercent(report.shares.answersWithoutSource),
          })}
        />
        <StatTile
          label={t("reports.quality.ocrFlagged")}
          value={formatNumber(totals.ocrFlagged)}
          hint={t("reports.quality.ocrFlaggedHint")}
        />
      </StatGrid>
      {citations > 0 ? (
        <ShareBar
          title={t("reports.quality.sharesTitle")}
          summary={t("reports.quality.sharesSummary", {
            supported: formatNumber(totals.supported),
            total: formatNumber(citations),
          })}
          segments={[
            {
              key: "supported",
              label: t("reports.quality.supported"),
              value: totals.supported,
              tone: 2,
            },
            {
              key: "unverified",
              label: t("reports.quality.unverified"),
              value: totals.unverified,
              tone: 3,
            },
            {
              key: "unsupported",
              label: t("reports.quality.unsupported"),
              value: totals.unsupported,
              tone: 4,
            },
          ]}
          labelHeader={t("reports.quality.status")}
          valueHeader={t("reports.quality.citations")}
        />
      ) : (
        <Empty>{t("reports.quality.sharesEmpty")}</Empty>
      )}
      {report.projects.length > 0 ? (
        <Section title={t("reports.quality.projectsTitle")}>
          <FigureTable
            caption={t("reports.quality.projectsTitle")}
            columns={[
              t("reports.project"),
              t("reports.quality.supported"),
              t("reports.quality.unverified"),
              t("reports.quality.unsupported"),
              t("reports.quality.withoutSource"),
            ]}
            rows={report.projects.map((p) => ({
              key: p.projectId ?? (p.standalone ? `standalone:${p.entityId}` : "personal"),
              cells: [
                projectName(p),
                formatNumber(p.supported),
                formatNumber(p.unverified),
                formatNumber(p.unsupported),
                `${formatNumber(p.answersWithoutSource)} / ${formatNumber(p.answers)}`,
              ],
            }))}
          />
        </Section>
      ) : null}
    </div>
  );
}

// ── Cost and capacity ────────────────────────────────────────────────────────────────────

export function CostTab({ report }: { report: CostReport }) {
  const { t } = useTranslation();
  const projectName = useProjectName();
  const roleLabel = (role: UsageRole) => t(`reports.cost.roleLabel.${role}`);
  const roles = report.roles.map((r) => r.role);
  const topRole = [...report.roles].sort((a, b) => b.costUsd - a.costUsd)[0];
  const { capacity } = report;
  return (
    <div className="flex flex-col gap-8">
      <Freshness freshness={report.freshness} synthetic={report.synthetic} />
      <StatGrid>
        <StatTile
          label={t("reports.cost.total")}
          value={formatUsd(report.totals.costUsd)}
          hint={
            report.totals.unknownCostCalls > 0
              ? t("reports.cost.unknown", { count: report.totals.unknownCostCalls })
              : undefined
          }
        />
        <StatTile
          label={t("reports.cost.inputTokens")}
          value={formatNumber(report.roles.reduce((n, r) => n + r.inputTokens, 0))}
        />
        <StatTile
          label={t("reports.cost.outputTokens")}
          value={formatNumber(report.roles.reduce((n, r) => n + r.outputTokens, 0))}
        />
      </StatGrid>
      {roles.length > 0 ? (
        <>
          <StackedBarChart
            title={t("reports.cost.byRoleTitle")}
            summary={t("reports.cost.byRoleSummary", {
              total: formatUsd(report.totals.costUsd),
              top: topRole ? roleLabel(topRole.role) : "",
            })}
            series={roles.map((role) => ({ key: role, label: roleLabel(role) }))}
            data={report.daily.map((d) => ({
              label: d.day,
              values: Object.fromEntries(d.roles.map((r) => [r.role, r.costUsd])),
            }))}
            formatValue={formatUsd}
            formatLabel={formatDay}
            labelHeader={t("reports.day")}
          />
          <Section title={t("reports.cost.roles")}>
            <FigureTable
              caption={t("reports.cost.roles")}
              columns={[
                t("reports.cost.role"),
                t("reports.cost.calls"),
                t("reports.cost.inputTokens"),
                t("reports.cost.outputTokens"),
                t("reports.cost.cost"),
              ]}
              rows={report.roles.map((r) => ({
                key: r.role,
                cells: [
                  roleLabel(r.role),
                  formatNumber(r.calls),
                  formatNumber(r.inputTokens),
                  formatNumber(r.outputTokens),
                  r.unknownCostCalls > 0
                    ? `${formatUsd(r.costUsd)} · ${t("reports.cost.unknown", { count: r.unknownCostCalls })}`
                    : formatUsd(r.costUsd),
                ],
              }))}
            />
          </Section>
        </>
      ) : (
        <Empty>{t("reports.empty")}</Empty>
      )}
      {/* Absent from a previous API revision during a canary deploy. */}
      {(report.tools ?? []).length > 0 ? (
        <Section title={t("reports.cost.toolsTitle")}>
          <FigureTable
            caption={t("reports.cost.toolsTitle")}
            columns={[t("reports.cost.tool"), t("reports.cost.calls"), t("reports.cost.cost")]}
            rows={(report.tools ?? []).map((tool) => ({
              key: tool.tool,
              cells: [
                t(`reports.cost.toolLabel.${tool.tool}`, { defaultValue: tool.tool }),
                formatNumber(tool.calls),
                tool.unknownCostCalls > 0
                  ? `${formatUsd(tool.costUsd)} · ${t("reports.cost.unknown", { count: tool.unknownCostCalls })}`
                  : formatUsd(tool.costUsd),
              ],
            }))}
          />
        </Section>
      ) : null}
      {/* Model cost per origin (MCP on its own line); absent from a previous API revision. */}
      {(report.origins ?? []).length > 0 ? (
        <Section title={t("reports.cost.originsTitle")}>
          <FigureTable
            caption={t("reports.cost.originsTitle")}
            columns={[t("reports.cost.origin"), t("reports.cost.calls"), t("reports.cost.cost")]}
            rows={(report.origins ?? []).map((o) => ({
              key: o.origin,
              cells: [
                t(`reports.cost.originLabel.${o.origin}`),
                formatNumber(o.calls),
                formatUsd(o.costUsd),
              ],
            }))}
          />
        </Section>
      ) : null}
      {capacity ? (
        <Section title={t("reports.cost.capacity")}>
          <div className="flex flex-col gap-4 rounded-lg border border-line bg-surface-raised p-4">
            <Meter
              label={t("reports.cost.ocrPages")}
              value={capacity.ocrPages.used}
              max={capacity.ocrPages.limit}
            />
            <Meter
              label={t("reports.cost.storage")}
              value={capacity.storageBytes.used}
              max={capacity.storageBytes.limit}
              formatValue={formatBytes}
              {...(capacity.storageBytes.limit === null && {
                valueText: `${formatBytes(capacity.storageBytes.used)} · ${t("reports.cost.noLimit")}`,
              })}
            />
            <Meter
              label={t("reports.cost.seats")}
              value={capacity.activeUsers.used}
              max={capacity.activeUsers.limit}
              {...(capacity.activeUsers.limit === null && {
                valueText: `${formatNumber(capacity.activeUsers.used)} · ${t("reports.cost.noLimit")}`,
              })}
            />
            <Meter
              label={t("reports.cost.modelSpend")}
              value={capacity.modelSpend.usd}
              max={capacity.modelSpend.limit}
              formatValue={formatUsd}
              {...(capacity.modelSpend.limit === null && {
                valueText: `${formatUsd(capacity.modelSpend.usd)} · ${t("reports.cost.noLimit")}`,
              })}
            />
            {capacity.modelSpend.exceeded ? (
              <Notice tone={capacity.modelSpend.hard ? "danger" : "warning"}>
                {capacity.modelSpend.hard
                  ? t("reports.cost.spendReachedHard")
                  : t("reports.cost.spendExceeded")}
              </Notice>
            ) : capacity.modelSpend.warning ? (
              <Notice tone="warning">{t("reports.cost.spendWarning")}</Notice>
            ) : null}
          </div>
        </Section>
      ) : (
        <p className="text-sm text-fg-secondary">
          {t("reports.cost.storedInScope", { size: formatBytes(report.storageBytes) })}
        </p>
      )}
      {report.projects.length > 0 ? (
        <Section title={t("reports.cost.projectsTitle")}>
          <FigureTable
            caption={t("reports.cost.projectsTitle")}
            columns={[t("reports.project"), t("reports.cost.cost")]}
            rows={report.projects.map((p) => ({
              key: p.projectId ?? (p.standalone ? `standalone:${p.entityId}` : "personal"),
              cells: [projectName(p), formatUsd(p.costUsd)],
            }))}
          />
        </Section>
      ) : null}
    </div>
  );
}

// ── Health and security ──────────────────────────────────────────────────────────────────

export function HealthTab({ report, auditLinks }: { report: HealthReport; auditLinks: boolean }) {
  const { t } = useTranslation();
  const { base } = useAdminApi();
  const projectName = useProjectName();
  const { totals } = report;
  const reason = (r: string) =>
    r === "other"
      ? t("reports.health.otherReason")
      : t(`files.failure.${r}`, { defaultValue: t("reports.health.otherReason") });
  const topReason = [...report.ingestionFailures].sort((a, b) => b.count - a.count)[0];
  const audited = (count: number, action: string) =>
    auditLinks && count > 0 ? (
      <Link
        href={auditPath(action, base)}
        className="text-fg-secondary underline underline-offset-4 hover:text-fg"
      >
        {t("reports.health.seeEvents")}
      </Link>
    ) : undefined;
  return (
    <div className="flex flex-col gap-8">
      <Freshness freshness={report.freshness} synthetic={report.synthetic} />
      <Section title={t("reports.health.ingestion")}>
        <StatGrid>
          <StatTile
            label={t("reports.health.ingestionFailures")}
            value={formatNumber(totals.ingestionFailures)}
          />
          <StatTile
            label={t("reports.health.uploading")}
            value={formatNumber(report.pending.uploading)}
          />
          <StatTile
            label={t("reports.health.processing")}
            value={formatNumber(report.pending.processing)}
          />
        </StatGrid>
        {topReason ? (
          <BarChart
            title={t("reports.health.failuresTitle")}
            summary={t("reports.health.failuresSummary", {
              count: formatNumber(totals.ingestionFailures),
              top: reason(topReason.reason).replace(/\.$/, ""),
            })}
            data={report.ingestionFailures.map((f) => ({
              label: reason(f.reason),
              value: f.count,
            }))}
            tone={4}
            labelHeader={t("reports.health.reason")}
            valueHeader={t("reports.health.ingestionFailures")}
          />
        ) : null}
      </Section>
      <Section title={t("reports.health.models")}>
        <StatGrid>
          <StatTile
            label={t("reports.health.modelFailures")}
            value={formatNumber(totals.modelFailures)}
          />
          <StatTile
            label={t("reports.health.modelUnavailable")}
            value={formatNumber(totals.modelUnavailable)}
          />
          <StatTile
            label={t("reports.health.modelFallbacks")}
            value={formatNumber(totals.modelFallbacks)}
          />
        </StatGrid>
      </Section>
      <Section title={t("reports.health.access")}>
        <StatGrid>
          <StatTile label={t("reports.health.notFound")} value={formatNumber(totals.notFound)} />
          <StatTile
            label={t("reports.health.downloadDenied")}
            value={formatNumber(totals.downloadDenied)}
          />
          <StatTile
            label={t("reports.health.flowDenied")}
            value={formatNumber(totals.flowDenied)}
          />
          <StatTile
            label={t("reports.health.toolCallsBlocked")}
            value={formatNumber(totals.toolCallsBlocked ?? 0)}
          />
          <StatTile
            label={t("reports.health.toolCallsDeclined")}
            value={formatNumber(totals.toolCallsDeclined ?? 0)}
          />
        </StatGrid>
      </Section>
      <Section title={t("reports.health.governance")}>
        <StatGrid>
          <StatTile
            label={t("reports.health.breakGlass")}
            value={formatNumber(totals.breakGlass)}
            hint={audited(totals.breakGlass, "project.break_glass")}
          />
          <StatTile
            label={t("reports.health.declassificationsCreated")}
            value={formatNumber(totals.declassificationsCreated)}
            hint={audited(totals.declassificationsCreated, "declassification.created")}
          />
          <StatTile
            label={t("reports.health.declassificationsWithdrawn")}
            value={formatNumber(totals.declassificationsWithdrawn)}
            hint={audited(totals.declassificationsWithdrawn, "declassification.withdrawn")}
          />
        </StatGrid>
      </Section>
      {report.projects.length > 0 ? (
        <Section title={t("reports.health.projectsTitle")}>
          <FigureTable
            caption={t("reports.health.projectsTitle")}
            columns={[
              t("reports.project"),
              t("reports.health.ingestionFailures"),
              t("reports.health.flowDenied"),
              t("reports.health.notFound"),
              t("reports.health.breakGlass"),
            ]}
            rows={report.projects.map((p) => ({
              key: p.projectId ?? (p.standalone ? `standalone:${p.entityId}` : "personal"),
              cells: [
                projectName(p),
                formatNumber(p.ingestionFailures),
                formatNumber(p.flowDenied),
                formatNumber(p.notFound),
                formatNumber(p.breakGlass),
              ],
            }))}
          />
        </Section>
      ) : null}
    </div>
  );
}
